import { useEffect, useRef, useState } from "react";
import type { TouchEvent as ReactTouchEvent } from "react";
import { startsOnHorizontalScroller } from "@/components/SwipeTabs";

export type SwipeSibling = { id: string; name: string };

// The gesture engine behind swiping between sibling pages (a maker's listings,
// the growers list). Tracks the finger, decides commit vs spring-back, and
// names the sibling sliding in; the caller renders the incoming panel and
// performs the navigation.
export function useSwipePager({
  prev,
  next,
  disabled = false,
  resetKey,
  onCommit,
}: {
  prev: SwipeSibling | null;
  next: SwipeSibling | null;
  disabled?: boolean;
  resetKey: string | undefined;
  onCommit: (target: SwipeSibling) => void;
}) {
  const rootRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<{
    x: number;
    y: number;
    dx: number;
    axis: "" | "h" | "v";
    ignore: boolean;
  } | null>(null);
  const [swipe, setSwipe] = useState({ dx: 0, animating: false });
  const [swipeBox, setSwipeBox] = useState({ width: 0, top: 0 });
  // Held in state rather than derived from dx: an abandoned swipe returns dx to
  // 0 immediately, and a derived panel would vanish instead of sliding back out.
  const [incoming, setIncoming] = useState<(SwipeSibling & { fromRight: boolean }) | null>(null);

  useEffect(() => {
    dragRef.current = null;
    setSwipe({ dx: 0, animating: false });
    setIncoming(null);
  }, [resetKey]);

  const onTouchStart = (event: ReactTouchEvent) => {
    if (disabled || event.touches.length !== 1 || (!prev && !next)) return;
    const root = rootRef.current;
    const touch = event.touches[0];
    // The incoming panel is fixed to the viewport, so it has to start where the
    // page's content starts on screen -- below the sticky header.
    const headerBottom = document.querySelector("header")?.getBoundingClientRect().bottom ?? 0;
    setSwipeBox({
      width: root?.clientWidth || window.innerWidth,
      top: Math.max(headerBottom, root?.getBoundingClientRect().top ?? 0),
    });
    dragRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      dx: 0,
      axis: "",
      // A sideways scroller (photo strip, chip row) owns its own drag.
      ignore: !root || startsOnHorizontalScroller(event.target, root),
    };
  };

  const onTouchMove = (event: ReactTouchEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.ignore || event.touches.length !== 1) return;
    const touch = event.touches[0];
    const dx = touch.clientX - drag.x;
    const dy = touch.clientY - drag.y;
    if (!drag.axis) {
      if (Math.abs(dx) < 12 && Math.abs(dy) < 12) return;
      drag.axis = Math.abs(dx) > Math.abs(dy) * 1.3 ? "h" : "v";
    }
    if (drag.axis !== "h") return;
    // Rubber-band at the ends so the first and last sibling feel like edges.
    const target = dx < 0 ? next : prev;
    drag.dx = target ? dx : dx * 0.25;
    setSwipe({ dx: drag.dx, animating: false });
    setIncoming(target ? { id: target.id, name: target.name, fromRight: dx < 0 } : null);
  };

  const onTouchEnd = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || drag.axis !== "h") {
      setSwipe({ dx: 0, animating: false });
      return;
    }
    const width = rootRef.current?.clientWidth || window.innerWidth;
    const target = drag.dx < 0 ? next : prev;
    if (target && Math.abs(drag.dx) > Math.min(120, width * 0.28)) {
      setSwipe({ dx: drag.dx < 0 ? -width : width, animating: true });
      window.setTimeout(() => onCommit(target), 160);
      return;
    }
    setSwipe({ dx: 0, animating: true });
    window.setTimeout(() => {
      setSwipe({ dx: 0, animating: false });
      setIncoming(null);
    }, 200);
  };

  return {
    rootRef,
    swipe,
    swipeBox,
    incoming,
    touchHandlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel: onTouchEnd,
    },
  };
}
